import { useCallback, useEffect, useLayoutEffect, useRef, type MouseEvent as ReactMouseEvent, type PointerEvent } from 'react';
import { shallow } from 'zustand/shallow';
import { getStoreState, useAppStore, type AppState, type Camera } from '../../store';
import { callAction } from '../../app/actions';
import { ROOT_GRADIENT_STOPS, toHex6 } from '../../lib/color';
import {
  cameraCentredOn,
  fogPath,
  frameOf,
  MINIMAP_FOG,
  minimapBox,
  minimapUnit,
  minimapViewBox,
  viewBoxAttr,
  type MinimapBox,
  type WorldRect,
} from '../../lib/minimap';
import type { Bounds } from '../../lib/camera';
import { DARK, LIGHT } from '../../theme/tokens';
import { boundsOf, moveCamera, nodeBoxOf, stageSize, subscribeCamera, zoomBy } from '../map/mapController';
import { mapContextOf } from '../map/mapContext';
import { computeWiresFromState } from '../map/WireLayer';

/**
 * The minimap (V0.53.4): the whole visible map in a card at the top right of the stage, with a
 * frame over the part the camera is showing.
 *
 * Chosen from a proof of concept the user tried every variant in, and built on three rules the
 * map already lives by:
 *
 *  - **It draws what the map draws, from the numbers the map draws from.** The marks are the
 *    boxes `fit()` frames (`nodeBoxOf`), the wires are the wire layer's own `d` strings
 *    (`computeWiresFromState`), and the visible set is `mapContextOf`'s. Nothing here measures
 *    anything or decides visibility, so the picture cannot disagree with the map; and because
 *    the card's coordinate space IS world space, a wire's path is reused byte for byte.
 *  - **The camera never renders it.** The frame is the camera's inverse (`frameOf`) written
 *    onto two `<rect>`s and one `<path>` from a store subscription, exactly as the wire layer
 *    applies its `transform`. A pan costs the minimap three attribute writes and no React.
 *  - **Its host is a sibling of `#stage`**, the heatmap legend's rule: the stage owns the map's
 *    pointer handling, and a card inside it would start a pan on every click.
 *
 * REACT RENDERS THE SHELL ONCE; THE PICTURE IS NOT A RENDER AT ALL. This is the wire layer's
 * drag pattern applied to the whole card, and it was arrived at the hard way. The first build
 * drew every mark as an SVG element through React, and a one-letter search — which reveals a
 * few hundred nodes — had React reconciling six hundred elements on every intermediate store
 * notification of that reveal; a deferred value still re-rendered once per notification. That
 * put the match count ~100 ms later than HEAD and lost `09-view-state`'s race between a reload
 * and the router's 300 ms URL debounce. So: React renders the card, a canvas and a three-element
 * SVG when the toggle is on, and nothing else, ever. A store subscription outside React watches
 * the picture's inputs and schedules ONE rebuild — after 300 ms of quiet once a picture exists,
 * at the first idle moment before then — which draws the canvas (hundreds of marks in a few
 * milliseconds, no DOM to reconcile) and writes the card's size, the viewBox and the counts as
 * attributes. However many times the store moves during a reveal, the picture is redrawn once,
 * after the storm. The frame stays live throughout, because the camera has its own path.
 * MEASURED, not assumed: `09-view-state`'s "previous work day" case requests its reload
 * 257–275 ms after the keystroke on a HEAD bundle, against the router's 300 ms `?q=` debounce
 * — some 30 ms of headroom on the dev machine — and every earlier shape of this component
 * (SVG through React, deferred, idle-rebuilt) spent enough of it to lose runs.
 *
 * TWO LAYERS, AND WHY. The PICTURE — the sheet, the wires, one mark per node — is the canvas;
 * the frame, its fog and its hit area are the SVG over it, so they stay crisp under the hover
 * growth and keep the viewBox the pointer maths reads.
 *
 * What a pointer does here: a click takes the camera to that point at its own zoom, through the
 * one animator (`moveCamera`), so it arrives the way every other deliberate move does;
 * dragging the frame pans (`panTo`, which is what also stops any move in flight — the animator
 * cancels itself on a write it did not make); the wheel zooms about the stage's centre, as the
 * two buttons do. All three are navigation, so none of them badges: the drag and the wheel
 * `saveLocal({quiet})` as the stage's own do, and a settled flight saves the view record alone.
 *
 * THE DIMMING HAS NO TIMER. The card rests at a third of its opacity and is bright for 1.8 s
 * after every camera write, but the 1.8 s is a CSS `transition-delay`, not a `setTimeout`:
 * `wake()` puts `.active` on and takes it off two frames later, once a style has been computed
 * with it, which snaps the card bright and re-arms the stylesheet's delayed fade. Two reasons.
 * A per-frame signal has no business going back through the store (the reason `wireBus` is a
 * callback), and — the one that decided it — a JS timer makes the card's state at any instant
 * a question of timing, which the pixel baselines would then photograph either way depending
 * on the machine. Playwright's `animations: 'disabled'` fast-forwards a pending transition to
 * its end, so every baseline sees the resting card, always.
 */

/** The values that decide the PICTURE. The camera is deliberately absent. */
function pictureInputsOf(s: AppState) {
  return {
    isLight: s.isLight,
    nodes: s.nodes,
    edges: s.edges,
    positions: s.positions,
    sizes: s.sizes,
    displayY: s.displayY,
    docsExpanded: s.docsExpanded,
    forceOpen: s.forceOpen,
    perm: s.perm,
    tagEdges: s.tagEdges,
  };
}

interface Mark {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  root: boolean;
  color: string;
}

interface Picture {
  bounds: Bounds;
  box: MinimapBox;
  vb: WorldRect;
  /** World units per CSS pixel of the card — what a radius or a stroke meant in pixels scales by. */
  unit: number;
  marks: Mark[];
  wires: { id: string; d: string; stroke: string }[];
}

function pictureOf(s: AppState): Picture | null {
  const ids = mapContextOf(s).visibleIds;
  if (!ids.length) return null;
  const bounds = boundsOf(s, ids);
  const box = minimapBox(bounds);
  const vb = minimapViewBox(bounds, box);
  const marks: Mark[] = [];
  for (const id of ids) {
    const b = nodeBoxOf(s, id);
    if (!b) continue;
    const n = s.nodes[id];
    // `toHex6`: a canvas cannot parse `var(--accent)`, and one stored colour once was that.
    marks.push({ id, ...b, root: !!n.root, color: toHex6(n.color) });
  }
  // The parent-child wires only. The dotted cross-links are a toggle of their own and read as
  // noise at this size; the wire layer's drag offset rides along, so a subtree being dragged
  // is drawn where it is, not where it was.
  const wires = computeWiresFromState(s).edges.map((w) => ({ id: w.id, d: w.d, stroke: w.stroke }));
  return { bounds, box, vb, unit: minimapUnit(vb, box), marks, wires };
}

/**
 * Backing pixels per CSS pixel, on top of the device's own: the card grows ×1.5 under the
 * pointer, and a canvas drawn at 1× would blur when it does.
 */
const OVERSAMPLE = 1.5;

/** The latest a scheduled rebuild may wait for an idle moment before it runs anyway. */
const REBUILD_TIMEOUT_MS = 250;

/**
 * How long the store has to be QUIET before a card that already has a picture redraws it.
 *
 * A reveal or a layout pass moves the store dozens of times in a few hundred milliseconds,
 * and even an idle callback lands in the gaps between those moves — gaps the rest of the page
 * (and the regression suite's own round trips) need. So the picture waits until the storm is
 * over and redraws once; the frame is live throughout, because the camera has its own path. A
 * third of a second behind a branch expanding is not visible against the camera's own move.
 */
const REBUILD_QUIET_MS = 300;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, Math.min(r, w / 2, h / 2));
  else ctx.rect(x, y, w, h);
}

/**
 * The picture, drawn. World coordinates throughout: the canvas transform is the SVG overlay's
 * viewBox applied by hand, so a mark and the frame over it agree to the pixel.
 */
function drawPicture(canvas: HTMLCanvasElement, p: Picture, isLight: boolean): void {
  const dpr = (typeof devicePixelRatio === 'number' ? devicePixelRatio : 1) * OVERSAMPLE;
  const W = Math.max(1, Math.round(p.box.w * dpr));
  const H = Math.max(1, Math.round(p.box.h * dpr));
  if (canvas.width !== W) canvas.width = W;
  if (canvas.height !== H) canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const k = W / p.vb.w;
  ctx.setTransform(k, 0, 0, k, -p.vb.x * k, -p.vb.y * k);
  const t = isLight ? LIGHT : DARK;

  // The map's own extent, as a sheet: where the card's spare room ends and the map begins.
  ctx.globalAlpha = 1;
  ctx.fillStyle = t.panel2;
  roundRect(ctx, p.bounds.a - 20, p.bounds.b - 20, p.bounds.c - p.bounds.a + 40, p.bounds.d - p.bounds.b + 40, 10);
  ctx.fill();

  // The wires: one CSS pixel wide whatever the scale, in the child's colour.
  ctx.lineWidth = p.unit;
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.45;
  for (const w of p.wires) {
    ctx.strokeStyle = w.stroke;
    ctx.stroke(new Path2D(w.d));
  }

  // One mark per node: its box, in its colour; the root in its gradient.
  for (const m of p.marks) {
    if (m.root) {
      const g = ctx.createLinearGradient(m.x, m.y, m.x + m.w, m.y + m.h);
      g.addColorStop(0, ROOT_GRADIENT_STOPS[0]);
      g.addColorStop(1, ROOT_GRADIENT_STOPS[1]);
      ctx.fillStyle = g;
      ctx.globalAlpha = 1;
    } else {
      ctx.fillStyle = m.color;
      ctx.globalAlpha = 0.72;
    }
    roundRect(ctx, m.x, m.y, m.w, m.h, 3 * p.unit);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** `requestIdleCallback` where the browser has it (Chromium, Firefox); a short timer where not. */
function requestIdle(fn: () => void): number {
  if (typeof requestIdleCallback === 'function') return requestIdleCallback(fn, { timeout: REBUILD_TIMEOUT_MS });
  return window.setTimeout(fn, 120);
}
function cancelIdle(handle: number): void {
  if (typeof cancelIdleCallback === 'function') cancelIdleCallback(handle);
  else clearTimeout(handle);
}

/** Screen → world, through the SVG's own matrix — which also accounts for the hover growth. */
function toWorld(inv: DOMMatrix, clientX: number, clientY: number): { x: number; y: number } {
  const p = new DOMPoint(clientX, clientY).matrixTransform(inv);
  return { x: p.x, y: p.y };
}

interface Gesture {
  /** Started on the frame — so movement pulls it. Anywhere else, movement is ignored. */
  onFrame: boolean;
  /** The card's matrix at pointerdown. Held for the whole gesture: see `.dragging` in the CSS. */
  inv: DOMMatrix;
  p0: { x: number; y: number };
  start: { x: number; y: number };
  view: Camera;
  moved: boolean;
}

/** A pointer that travelled less than this is a click, whatever else it did. */
const CLICK_SLOP = 3;

export function Minimap(_: { host: HTMLElement }) {
  // The ONE store read that renders: whether the card exists at all.
  const on = useAppStore((s) => s.minimapOn);
  // "Keep it bright" (V0.54, the card's right-click menu). Read, but written onto the card as a
  // CLASS from an effect, never through `className`: the card also carries `.active` and
  // `.dragging`, written imperatively, and a re-render of `className` would wipe them.
  const bright = useAppStore((s) => s.minimapBright);

  const cardRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const frameRef = useRef<SVGRectElement>(null);
  const hitRef = useRef<SVGRectElement>(null);
  const fogRef = useRef<SVGPathElement>(null);
  /** The viewBox the frame and the fog are drawn in — the last rebuild's. */
  const vbRef = useRef<WorldRect | null>(null);
  const gesture = useRef<Gesture | null>(null);
  /** The stage's size, kept by the observer below so a camera write never has to measure. */
  const stageBox = useRef<{ width: number; height: number } | null>(null);
  const wakeFrame = useRef(0);
  const rebuildHandle = useRef(0);
  const quietTimer = useRef(0);

  /** The frame and the fog for this camera — attribute writes, no render, no measurement. */
  const applyCamera = useCallback((view: Camera) => {
    const size = stageBox.current ?? stageSize();
    const vb = vbRef.current;
    if (!size || !vb) return;
    const f = frameOf(view, size);
    for (const el of [frameRef.current, hitRef.current]) {
      if (!el) continue;
      el.setAttribute('x', String(f.x));
      el.setAttribute('y', String(f.y));
      el.setAttribute('width', String(f.w));
      el.setAttribute('height', String(f.h));
    }
    fogRef.current?.setAttribute('d', fogPath(vb, f));
  }, []);

  /**
   * The picture, from the store as it is NOW, onto the canvas and the card — see the header.
   * A map with nothing visible hides the card rather than drawing an empty sheet.
   */
  const rebuild = useCallback(() => {
    const card = cardRef.current;
    const canvas = canvasRef.current;
    const svg = svgRef.current;
    if (!card || !canvas || !svg) return;
    const s = getStoreState();
    const p = pictureOf(s);
    vbRef.current = p?.vb ?? null;
    if (!p) {
      card.hidden = true;
      return;
    }
    card.hidden = false;
    card.style.width = `${p.box.w}px`;
    card.style.height = `${p.box.h}px`;
    // The picture has no DOM of its own, so the card says what it drew — for the spec, and for
    // anyone reading the element.
    card.dataset.marks = String(p.marks.length);
    card.dataset.wires = String(p.wires.length);
    svg.setAttribute('viewBox', viewBoxAttr(p.vb));
    frameRef.current?.setAttribute('rx', String(2 * p.unit));
    fogRef.current?.setAttribute('fill', s.isLight ? MINIMAP_FOG.light : MINIMAP_FOG.dark);
    drawPicture(canvas, p, s.isLight);
    applyCamera(s.view);
  }, [applyCamera]);

  /** One rebuild, at the next idle moment, however many changes ask for it before then. */
  const rebuildWhenIdle = useCallback(() => {
    if (rebuildHandle.current) return;
    rebuildHandle.current = requestIdle(() => {
      rebuildHandle.current = 0;
      rebuild();
    });
  }, [rebuild]);

  /**
   * The store moved. A card with nothing drawn yet — boot, or just switched on — draws at the
   * first idle moment; one that already shows a picture waits for `REBUILD_QUIET_MS` of quiet,
   * the timer restarting on every further change, so a storm costs it nothing until it is over.
   */
  const scheduleRebuild = useCallback(() => {
    const card = cardRef.current;
    if (!card || card.hidden) {
      rebuildWhenIdle();
      return;
    }
    if (quietTimer.current) clearTimeout(quietTimer.current);
    quietTimer.current = window.setTimeout(() => {
      quietTimer.current = 0;
      rebuildWhenIdle();
    }, REBUILD_QUIET_MS);
  }, [rebuildWhenIdle]);

  /**
   * Snap the card bright and re-arm its delayed fade — see the header.
   *
   * TWO FRAMES, NOT ONE. A rAF callback runs at the START of a frame, before its style pass, so
   * a one-hop removal would take the class off before the browser had ever computed a style
   * with it — an add-and-remove the engine coalesces into "nothing changed", and no transition
   * starts. The second hop lands after that pass, so the removal transitions from .75. (A
   * `getComputedStyle` read between the two writes does the same in one tick, at the price of
   * a forced style flush on every camera write; this costs nothing.) A write every frame — a
   * pan — keeps re-arming the removal, so the card simply stays bright until the writes stop.
   */
  const wake = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.classList.add('active');
    if (wakeFrame.current) cancelAnimationFrame(wakeFrame.current);
    wakeFrame.current = requestAnimationFrame(() => {
      wakeFrame.current = requestAnimationFrame(() => {
        wakeFrame.current = 0;
        cardRef.current?.classList.remove('active');
      });
    });
  }, []);

  // The card has just appeared: draw it now, before paint, so a toggle shows a picture and not
  // a blank card for an idle moment. From here on the subscription below owns the picture.
  useLayoutEffect(() => {
    if (on) rebuild();
  }, [on, rebuild]);

  // The picture's inputs, watched OUTSIDE React and coalesced into one rebuild after the quiet.
  useEffect(() => {
    if (!on) return;
    const stop = useAppStore.subscribe(pictureInputsOf, scheduleRebuild, { equalityFn: shallow });
    return () => {
      stop();
      if (quietTimer.current) clearTimeout(quietTimer.current);
      quietTimer.current = 0;
      if (rebuildHandle.current) cancelIdle(rebuildHandle.current);
      rebuildHandle.current = 0;
    };
  }, [on, scheduleRebuild]);

  // The camera, by subscription. Also what wakes the card when it appears.
  useEffect(() => {
    if (!on) return;
    wake();
    const stop = subscribeCamera((view) => {
      applyCamera(view);
      wake();
    });
    return () => {
      stop();
      if (wakeFrame.current) cancelAnimationFrame(wakeFrame.current);
      wakeFrame.current = 0;
    };
  }, [on, applyCamera, wake]);

  // The stage's size is half of the frame, and this is the ONE place it is measured: once on
  // mount and then from the observer, so a window resize moves the frame without a camera
  // write and a camera write never forces a layout to find out how big the stage is.
  useEffect(() => {
    const stage = document.getElementById('stage');
    if (!stage) return;
    stageBox.current = { width: stage.clientWidth, height: stage.clientHeight };
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) stageBox.current = { width: r.width, height: r.height };
      applyCamera(getStoreState().view);
    });
    ro.observe(stage);
    return () => ro.disconnect();
  }, [applyCamera]);

  // The wheel needs a NON-passive listener to keep the page from scrolling, which React's
  // synthetic `onWheel` cannot promise; attached to the card directly for that reason alone.
  useEffect(() => {
    const card = cardRef.current;
    if (!on || !card) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 1.1 : 0.9);
      callAction('saveLocal', { quiet: true }); // a wheel notch is view-only, as on the stage
    };
    card.addEventListener('wheel', onWheel, { passive: false });
    return () => card.removeEventListener('wheel', onWheel);
  }, [on]);

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const inv = e.currentTarget.getScreenCTM()?.inverse();
    if (!inv) return;
    gesture.current = {
      onFrame: e.target === hitRef.current,
      inv,
      p0: toWorld(inv, e.clientX, e.clientY),
      start: { x: e.clientX, y: e.clientY },
      view: { ...getStoreState().view },
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    if (!g) return;
    if (!g.moved && Math.hypot(e.clientX - g.start.x, e.clientY - g.start.y) > CLICK_SLOP) {
      g.moved = true;
      if (g.onFrame) {
        cardRef.current?.classList.add('dragging');
        callAction('hideHint'); // pulling the frame is a pan: the hint steps aside as for the stage's
      }
    }
    if (!g.moved || !g.onFrame) return;
    // Pull the frame by the world distance the pointer covered; the camera goes the other way.
    const p1 = toWorld(g.inv, e.clientX, e.clientY);
    getStoreState().panTo(g.view.x - (p1.x - g.p0.x) * g.view.k, g.view.y - (p1.y - g.p0.y) * g.view.k);
  };

  const onPointerUp = (e: PointerEvent<SVGSVGElement>) => {
    const g = gesture.current;
    gesture.current = null;
    cardRef.current?.classList.remove('dragging');
    if (!g) return;
    if (g.moved) {
      // A pan is a camera move: the view record, quietly, exactly as the stage's own pan.
      if (g.onFrame) callAction('saveLocal', { quiet: true });
      return;
    }
    // A click: take the camera there, at the reader's own zoom, the way it moves everywhere else.
    const inv = e.currentTarget.getScreenCTM()?.inverse();
    const size = stageSize();
    if (!inv || !size) return;
    const p = toWorld(inv, e.clientX, e.clientY);
    moveCamera(cameraCentredOn(p, size, getStoreState().view.k), {
      animate: true,
      onEnd: (completed) => {
        if (completed) callAction('saveViewState');
      },
    });
  };

  const onPointerCancel = () => {
    gesture.current = null;
    cardRef.current?.classList.remove('dragging');
  };

  useEffect(() => {
    cardRef.current?.classList.toggle('bright', bright);
  }, [bright, on]);

  /** The card's own right-click menu (V0.54): Fit, Keep it bright, Hide. Shift keeps the browser's. */
  const onContextMenu = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.shiftKey) return;
    e.preventDefault();
    callAction('openContextMenu', { kind: 'minimap' }, e.clientX, e.clientY);
  };

  if (!on) return null;

  return (
    // Hidden until the first rebuild has something to draw; sized, labelled and given its
    // viewBox by `rebuild`, never by a render.
    <div ref={cardRef} className="minimap" role="group" aria-label="Minimap" hidden onContextMenu={onContextMenu}>
      <canvas ref={canvasRef} className="mm-canvas" aria-hidden="true" />
      <svg
        ref={svgRef}
        viewBox="0 0 1 1"
        preserveAspectRatio="xMidYMid meet"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {/* Written by `applyCamera` and `rebuild`, never by a render: the fog, the frame, and
            its hit area. */}
        <path ref={fogRef} className="mm-fog" fillRule="evenodd" d="" />
        <rect ref={frameRef} className="mm-frame" />
        <rect ref={hitRef} className="mm-hit" />
      </svg>
    </div>
  );
}
